import { notifyMfaEnrollmentRequiredFromResponse } from './mfa-enrollment-client';
import { notifyUnauthenticated } from './use-session-broadcast';

import type { ThemePreference } from '@socialgoodsoftware/theme';
import type { Organization } from './types';
import type { UserMenuItem } from './types';

/**
 * The account snapshot every product shell renders: user menu, avatar, and the
 * persisted theme preference, from GET /api/user/account.
 */
export type ShellUserAccount = {
  firstName: string;
  lastName: string;
  email: string;
  profilePhotoUrl?: string | null;
  theme: ThemePreference;
  accountHubBaseUrl: string;
  accountHubLogoutUrl: string;
  menu: UserMenuItem[][];
};

/** Active organization selection from /api/validate-session (name + precedence; never oid). */
export type ShellSessionState = {
  valid: boolean;
  name?: string | null;
  precedence?: number | null;
};

export type ShellApiOptions = {
  logoutUrl: string;
};

/**
 * The one fetch layer behind every product shell: account snapshot, organization
 * list, and the session selection, with the shared 401 broadcast and the MFA
 * enrollment redirect on every call.
 */
export function createShellApi(options: ShellApiOptions) {
  async function getJSON<T>(url: string): Promise<T> {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) {
      if (response.status === 401) {
        notifyUnauthenticated(options.logoutUrl);
      } else {
        await notifyMfaEnrollmentRequiredFromResponse(response);
      }
      throw new Error(`Request failed: ${response.status}`);
    }
    return response.json() as Promise<T>;
  }

  async function getAccount(): Promise<ShellUserAccount> {
    return getJSON<ShellUserAccount>('/api/user/account');
  }

  async function getOrganizations(): Promise<Organization[]> {
    const response = await getJSON<{ organizations: Organization[] }>('/api/user/organizations');
    return response.organizations;
  }

  async function getSessionState(): Promise<ShellSessionState> {
    return getJSON<ShellSessionState>('/api/validate-session');
  }

  return {
    getAccount: getAccount,
    getOrganizations: getOrganizations,
    getSessionState: getSessionState,
  };
}
